import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

import { environment } from '../../../../environments/environment';

@Component({
  selector: 'umkt-chatbot',
  standalone: true,
  imports: [CommonModule],
  template: '',
})
export class ChatbotComponent implements OnInit, OnDestroy {
  private readonly botUrl = environment.chatBotUrl;
  private readonly botLoadTimeoutMs = 10000;
  private messageListener?: (event: MessageEvent) => void;
  private loadTimeoutId?: number;
  private isDestroyed = false;

  ngOnInit(): void {
    if (!environment.production) {
      return;
    }

    this.isDestroyed = false;
    this.startBot();
    this.setupMessageListener();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.clearLoadTimeout();
    this.removeBot();
    this.removeMessageListener();
  }

  private startBot(): void {
    if (document.getElementById('chatbotFrame')) {
      return;
    }

    const iframe = document.createElement('iframe');

    iframe.setAttribute('src', this.botUrl);
    iframe.setAttribute('width', '66');
    iframe.setAttribute('height', '70');
    iframe.setAttribute('id', 'chatbotFrame');
    iframe.classList.add('chatbot');
    iframe.style.zIndex = '999999';
    iframe.style.position = 'fixed';
    iframe.style.border = '0';
    iframe.style.right = '16px';
    iframe.style.bottom = '30px';
    iframe.style.overflow = 'hidden';
    iframe.style.background = 'transparent';
    iframe.style.transition =
      'width 0.3s cubic-bezier(0.55, 0, 0.55, 0.2), height 0.3s cubic-bezier(0.55, 0, 0.55, 0.2)';

    iframe.addEventListener('load', () => this.clearLoadTimeout());
    iframe.addEventListener('error', () => this.handleBotLoadFailure());

    this.loadTimeoutId = window.setTimeout(() => {
      this.loadTimeoutId = undefined;
      if (!this.isDestroyed) {
        console.warn('Chatbot failed to load within timeout');
        this.removeBot();
      }
    }, this.botLoadTimeoutMs);

    document.body.appendChild(iframe);
  }

  private handleBotLoadFailure(): void {
    console.warn('Chatbot unavailable');
    this.clearLoadTimeout();
    this.removeBot();
  }

  private clearLoadTimeout(): void {
    if (this.loadTimeoutId !== undefined) {
      window.clearTimeout(this.loadTimeoutId);
      this.loadTimeoutId = undefined;
    }
  }

  private setupMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'string') {
        return;
      }

      try {
        const data = JSON.parse(event.data);
        const iframe = document.getElementById('chatbotFrame') as HTMLIFrameElement;

        if (!iframe) {
          return;
        }

        if (data.action) {
          this.resizeIframe(iframe, 380, 650);
        } else {
          iframe.setAttribute('scrolling', 'no');
          this.resizeIframe(iframe, 66, 70);
        }
      } catch {
        // Ignora mensagens que não são do chatbot
      }
    };

    window.addEventListener('message', this.messageListener);
  }

  private resizeIframe(iframe: HTMLIFrameElement, width: number, height: number): void {
    iframe.style.willChange = 'width, height';
    iframe.style.width = `${width}px`;
    iframe.style.height = `${height}px`;
    iframe.setAttribute('width', String(width));
    iframe.setAttribute('height', String(height));

    window.setTimeout(() => {
      iframe.style.willChange = 'auto';
    }, 350);
  }

  private removeMessageListener(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
  }

  private removeBot(): void {
    document.getElementById('chatbotFrame')?.remove();
  }
}
